// src/pages/gt3x/Reports.jsx
import { useEffect, useMemo, useState } from "react";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { safeFetchSheet } from "../../lib/sheetsSafe";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const SCHEDULE_RANGE = "Schedule!A1:D";

// Wir holen immer den kompletten Bereich, weil wir aus zwei Blöcken lesen
function rangeFor(race) {
    return `R${race}_INCIDENTS!A1:Z300`;
}

// Spaltenhilfen: A -> 0, B -> 1, ..., Z -> 25
function colIndexFromLetters(letters) {
    let n = 0;
    const s = String(letters).toUpperCase().trim();
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 65 || code > 90) continue; // nur A-Z
        n = n * 26 + (code - 64);
    }
    return n - 1; // 0-based
}

// Subset: bestimmte Spalten aus Header + Zeilen herausziehen
function subsetByLetters(header, rows, letters) {
    const indices = letters.map(colIndexFromLetters);
    const safeHeader = Array.isArray(header) ? header : [];

    const subHeader = indices.map((idx, k) => {
        const cell = safeHeader[idx];
        return cell !== undefined && cell !== null && String(cell).trim() !== ""
            ? cell
            : letters[k];
    });

    const safeRows = Array.isArray(rows) ? rows : [];
    const subRows = safeRows.map((row) =>
        indices.map((idx) => (row && row[idx] !== undefined ? row[idx] : ""))
    );

    return { header: subHeader, rows: subRows };
}

// Führende komplett leere Zeilen entfernen
function trimLeadingEmptyRows(rows) {
    if (!Array.isArray(rows)) return [];
    let start = 0;
    while (start < rows.length) {
        const r = rows[start] || [];
        const hasValue = r.some(
            (cell) => cell !== null && cell !== undefined && String(cell).trim() !== ""
        );
        if (hasValue) break;
        start++;
    }
    return rows.slice(start);
}

// Einfache Table-Komponente
function SimpleTable({
                         header,
                         rows,
                         emptyMessage,
                         wrapCols,
                         colStyles,
                         boldCols,
                     }) {
    if (!header || header.length === 0) return null;

    const hasRows = Array.isArray(rows) && rows.length > 0;
    const wrapSet = Array.isArray(wrapCols) ? new Set(wrapCols) : null;
    const boldSet = Array.isArray(boldCols) ? new Set(boldCols) : null;

    return (
        <div className="fancyTable">
            <table className="tbl">
                {Array.isArray(colStyles) && colStyles.length ? (
                    <colgroup>
                        {header.map((_, i) => (
                            <col
                                key={i}
                                style={
                                    colStyles[i]
                                        ? {
                                            width: colStyles[i].width,
                                            minWidth: colStyles[i].minWidth,
                                        }
                                        : undefined
                                }
                            />
                        ))}
                    </colgroup>
                ) : null}

                <thead>
                <tr>
                    {header.map((h, i) => (
                        <th
                            key={i}
                            className={wrapSet?.has(i) ? "wrap-cell-head" : ""}
                        >
                            {h}
                        </th>
                    ))}
                </tr>
                </thead>

                <tbody>
                {hasRows ? (
                    rows.map((r, ri) => (
                        <tr key={ri}>
                            {r.map((cell, ci) => {
                                const hName = String(header?.[ci] ?? "").toLowerCase();

                                // Zahl prüfen
                                const numVal = Number(String(cell ?? "").replace(",", "."));
                                const isNumeric = String(cell ?? "").trim() !== "" && !Number.isNaN(numVal);

                                // ✅ Penalty / Strafsekunden rot: Header enthält "pen" oder "strafe"
                                const isPenaltyCol =
                                    hName.includes("pen") ||
                                    hName.includes("penalty") ||
                                    hName.includes("sekund") ||
                                    hName.includes("punkte");

                                const penaltyRed = isPenaltyCol && isNumeric && numVal > 0;

                                return (
                                    <td
                                        key={ci}
                                        className={[
                                            wrapSet?.has(ci) ? "wrap-cell" : "",
                                            penaltyRed ? "penalty-red" : "",
                                            boldSet?.has(ci) ? "cell-bold cell-accent" : "",   // ✅ NEU
                                        ].join(" ").trim()}
                                    >
                                        {cell}
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={header.length} className="align-left text-dim">
                            {emptyMessage || "Noch keine Einträge vorhanden."}
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

// Header-Beautifier: gezielt kürzen/umbauen + Umbruch erzwingen
function prettifyHeader(label) {
    const raw = String(label ?? "").trim();
    const low = raw.toLowerCase();

    // exakt / halbwegs robust matchen
    if (low === "zeitstempel" || low === "timestamp") return "Zeit-stempel";

    // "Car 1 # Name" / "Car 1 Name" / ähnliche Schreibweisen -> Car #1
    if (low.includes("car 1") && (low.includes("#") || low.includes("name"))) return "Car #1";
    if (low.includes("car 2") && (low.includes("#") || low.includes("name"))) return "Car #2";

    if (low.includes("regelwerk") && low.includes("incident")) return "Regelwerk";

    if (low.includes("incident") && low.includes("sekund")) return "Straf-sekunden";
    if (low.includes("protest") && low.includes("sekund")) return "Straf-sekunden";
    if (low.includes("strafpunkte") && low.includes("incident")) return "Straf-punkte";
    if (low.includes("protest") && low.includes("id")) return "ID";
    if (low.includes("id") && low.includes("(1-99)")) return "ID";
    if (low.includes("protest") && low.includes("zeitstempel")) return "Zeit-stempel";


    if (low.includes("strafe") && low.includes("fahrer") && low.includes("incident")) return "Strafe für";

    // fallback: unverändert
    return raw;
}

// mapped Header array
function prettifyHeaderRow(headerArr) {
    return (headerArr || []).map(prettifyHeader);
}

export default function Reports() {
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);

    // ✅ nur noch 2 Tabs
    const [view, setView] = useState("INCIDENTS"); // "INCIDENTS" | "PROTESTS"

    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // ✅ Auto-Default über ResultsFrom (Schedule Spalte D)
    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    async ({ range }) => {
                        const r = await safeFetchSheet(range);
                        if (!r.ok) throw new Error(r.message || "Schedule fetch failed");
                        return r.data;
                    },
                    SCHEDULE_RANGE,
                    "results"
                );

                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto race failed (reports)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    useEffect(() => {
        if (!race) return;

        (async () => {
            setLoading(true);
            setErr("");

            const range = rangeFor(race);
            const res = await safeFetchSheet(range);

            if (!res.ok) {
                setErr(res.message || "Fehler beim Laden.");
                setData({ header: [], rows: [] });
                setLoading(false);
                return;
            }

            const d = res.data;
            let header = [];
            let rows = [];

            if (Array.isArray(d?.header) && Array.isArray(d?.rows)) {
                header = d.header;
                if (Array.isArray(d.rows[0])) rows = d.rows;
                else rows = d.rows.map((rowObj) => header.map((h) => rowObj[h]));
            } else if (Array.isArray(d?.values) && d.values.length) {
                const [head, ...body] = d.values;
                header = head;
                rows = body;
            } else {
                header = [];
                rows = [];
            }

            setData({ header, rows });
            setLoading(false);
        })();
    }, [race]);

    // ✅ Incidents = A..J
    const incidentsTable = useMemo(() => {
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        const subset = subsetByLetters(header, rows, ["A","B","C","D","E","F","G","H","I","J"]);

        return {
            header: prettifyHeaderRow(subset.header),
            rows: trimLeadingEmptyRows(subset.rows),
        };
    }, [data.header, data.rows]);

    // ✅ Proteste = M..X
    const protestsTable = useMemo(() => {
        const header = data.header ?? [];
        const rows = data.rows ?? [];
        const subset = subsetByLetters(header, rows, ["M","N","O","P","Q","R","S","T","U","V","W","X"]);

        return {
            header: prettifyHeaderRow(subset.header),
            rows: trimLeadingEmptyRows(subset.rows),
        };
    }, [data.header, data.rows]);

    const viewLabel = view === "INCIDENTS" ? "Incidents" : "Proteste";

    // ✅ alle Spalten wrappen (jede Zelle darf umbrechen)
    const wrapAll = (len) => Array.from({ length: len }, (_, i) => i);

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                showSession={false}
            />

            <div
                className="card"
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <h2>Rennkommission</h2>
                    <p className="muted">
                        <strong>{viewLabel}</strong>
                    </p>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className={`button ${view === "INCIDENTS" ? "button--primary" : "button--secondary"}`}
                        onClick={() => setView("INCIDENTS")}
                    >
                        Incidents
                    </button>
                    <button
                        className={`button ${view === "PROTESTS" ? "button--primary" : "button--secondary"}`}
                        onClick={() => setView("PROTESTS")}
                    >
                        Proteste
                    </button>
                </div>
            </div>

            {err && (
                <div className="card">
                    <p className="muted">{err}</p>
                </div>
            )}
            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !err && (
                <>
                    {view === "INCIDENTS" && incidentsTable && (
                        <div className="reports-table reports-table--incidents">
                            <SimpleTable
                                header={incidentsTable.header}
                                rows={incidentsTable.rows}
                                emptyMessage="Noch keine Incidents eingetragen."
                                wrapCols={wrapAll(incidentsTable.header.length)}
                                colStyles={[
                                    { width: "50px",  minWidth: "50px"  },   // A schmal
                                    { width: "86px", minWidth: "85px" },   // B
                                    { width: "120px",  minWidth: "120px"  },   // C
                                    { width: "110px", minWidth: "110px"  },   // D
                                    { width: "110px", minWidth: "110px" },   // E
                                    { width: "min(46vw, 760px)", minWidth: "360px" }, // F breit + MIN!
                                    { width: "140px", minWidth: "140px" },   // G
                                    { width: "100px", minWidth: "100px" },   // H
                                    { width: "100px", minWidth: "100px" },   // I
                                    { width: "140px", minWidth: "140px" },   // J
                                ]}
                                boldCols={[9]} // ✅ J fett
                            />
                        </div>
                    )}

                    {view === "PROTESTS" && protestsTable && (
                        <div className="reports-table reports-table--protests">
                            <SimpleTable
                                header={protestsTable.header}
                                rows={protestsTable.rows}
                                emptyMessage="Noch keine Fahrerproteste eingereicht."
                                wrapCols={wrapAll(protestsTable.header.length)}
                                colStyles={[
                                    { width: "50px",  minWidth: "50px"  },   // M
                                    { width: "86px", minWidth: "85px" },   // N
                                    { width: "70px",  minWidth: "70px"  },   // O
                                    { width: "110px", minWidth: "110px" },   // P
                                    { width: "70px",  minWidth: "70px"  },   // Q
                                    { width: "110px", minWidth: "110px" },   // R
                                    { width: "min(46vw, 670px)", minWidth: "300px" }, // S breit + MIN
                                    { width: "min(46vw, 670px)", minWidth: "300px" }, // T breit + MIN
                                    { width: "140px", minWidth: "140px" },   // U
                                    { width: "100px", minWidth: "100px" },   // V
                                    { width: "100px", minWidth: "100px" },   // W
                                    { width: "140px", minWidth: "140px" },   // X
                                ]}
                                boldCols={[11]} // ✅ X fett
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
}